// --------------------------------------------------------------
// Created On: 2021-06-08
// Author: Zachary Thomas
//
// Last Modified: 2026-10-16
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState, useEffect } from "react";
import EmailAndPasswordLoginForm from "./EmailAndPasswordLoginForm/EmailAndPasswordLoginForm";
import MfaRegistrationPhoneNumberForm from "./MfaRegistrationPhoneNumberForm/MfaRegistrationPhoneNumberForm";
import MfaRegistrationVerificationForm from "./MfaRegistrationVerificationForm/MfaRegistrationVerificationForm";
import MfaLoginForm from "./MfaLoginForm/MfaLoginForm";
import Spinner from "../../components/Spinner/Spinner";
import passwordIsValid from "../../utilities/passwordIsValid";
import { useNavigate } from "react-router-dom";
import apiRequest from "../../utilities/api/apiRequest";
import getApiError from "../../utilities/api/getApiError";
import { API } from "../../constants/miscellaneous";
import useReduxDispatch from "src/hooks/useReduxDispatch";
import { setIdToken } from "src/redux/features/idTokenSlice";
import { setAccessToken } from "src/redux/features/accessTokenSlice";
import { setRefreshToken } from "src/redux/features/refreshTokenSlice";
import { setSessionToken } from "src/redux/features/sessionTokenSlice";
import EulaModal from "../../components/EulaModal/EulaModal";
import ChangeTemporaryPasswordForm from "./ChangeTemporaryPasswordForm/ChangeTemporaryPasswordForm";
import useReduxSelector from "../../hooks/useReduxSelector";
import { setCurrentUser } from "src/redux/features/currentUserSlice";
import PostAuthenticateResponseBody from "src/types/api/PostAuthenticateResponseBody";
import styles from "./LoginPage.module.scss";

// Page for users to login to their account.
export default function LoginPage(props: Props): Component {
  const [currentLoginMode, setCurrentLoginMode] = useState<LoginMode>("ENTER_EMAIL_AND_PASSWORD");
  const [showEulaAgreementModal, setShowEulaAgreementModal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [pendingIdToken, setPendingIdToken] = useState<string | null>(null);
  const [emailAddress, setEmailAddress] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [newConfirmationPassword, setNewConfirmationPassword] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [callingCode, setCallingCode] = useState<string>("1");
  const [verificationCode, setVerificationCode] = useState<string>("");
  const [mfaCode, setMfaCode] = useState<string>("");
  const dispatch = useReduxDispatch();
  const navigate = useNavigate();
  const accessToken = useReduxSelector((state) => state.accessToken);
  const refreshToken = useReduxSelector((state) => state.refreshToken);
  const sessionToken = useReduxSelector((state) => state.sessionToken);
  const currentUser = useReduxSelector((state) => state.currentUser);
  const mobileNewPasswordRequired = useReduxSelector((state) => state.mobileNewPasswordRequired);
  const mobileMfaChallengeRequired = useReduxSelector((state) => state.mobileMfaChallengeRequired);
  const mobileMfaSetupRequired = useReduxSelector((state) => state.mobileMfaSetupRequired);

  // On initial page load, if there is an authorization error, update the error message.
  useEffect(() => {
    if (props.authorizationError) {
      setErrorMessage("Your session has expired. Please login to continue.");
    } else if (props.policyError) {
      setErrorMessage("EULA and privacy policy must be accepted to be able to login.");
    } else {
      setErrorMessage("");
    }
  }, [props.authorizationError, props.policyError]);

  // Clear tokens on load, unless we routed to this page with the intent to automate the authentication process.
  useEffect(() => {
    if (!props.automateAuthentication) {
      dispatch(setIdToken(null));
      dispatch(setAccessToken(null));
      dispatch(setRefreshToken(null));
      dispatch(setSessionToken(null));
      dispatch(
        setCurrentUser({
          userId: 0,
          userName: "",
          emailAddress: "",
          originalCompanyId: 0,
          originalCompanyName: "",
          originalPermissions: [],
          companyId: 0,
          companyName: "",
          userCompanyPath: [],
          isCompanyHidden: false,
          permissions: [],
          isPackager: false,
          mfaEnabled: false,
        })
      );
    }
  }, [props.automateAuthentication]);

  // If we were routed to this page to automate the authentication process, start authenticating.
  useEffect(() => {
    if (props.automateAuthentication) {
      setEmailAddress(currentUser.emailAddress);
      if (refreshToken !== null) {
        // If we have a refresh token, we can re-authenticate to continue the process that the mobile app started.
        void handleSubmitRefreshToken(currentUser.emailAddress, refreshToken);
      } else if (sessionToken !== null) {
        if (mobileNewPasswordRequired) {
          setCurrentLoginMode("ENTER_PERMANENT_PASSWORD");
        } else if (mobileMfaSetupRequired) {
          setCurrentLoginMode("ENTER_MFA_PHONE_NUMBER");
        } else if (mobileMfaChallengeRequired) {
          setCurrentLoginMode("ENTER_MFA_CODE");
        }
      }
    }
  }, [props.automateAuthentication]);

  // Don't allow user to login if they decline the EULA.
  function declineEula(): void {
    handleCancel();
    setErrorMessage("EULA and privacy policy must be accepted to login.");
    setShowEulaAgreementModal(false);
  }

  // Accept the current EULA.
  async function acceptEula(): Promise<void> {
    if (pendingIdToken !== null) {
      dispatch(setIdToken(pendingIdToken));
    }

    setLoading(true);
    const [response, responseBody] = (await apiRequest(`${API}/accept/eula`, "POST", null)) as [
      Response,
      EulaResponseBody
    ];
    setLoading(false);

    setShowEulaAgreementModal(false);
    if (response.ok && responseBody) {
      finalizeUserLogin(true, props.redirectUrl, pendingIdToken);
    } else {
      setErrorMessage(await getApiError(response, "Unable to accept EULA and privacy policy."));
    }
  }

  // Attempts the first step of logging in, which is submitting a users email address and password.
  async function handleSubmitEmailAndPassword(emailAddress: string, password: string): Promise<void> {
    if (emailAddress.length === 0) {
      setErrorMessage("Please enter an email address.");
    } else if (password.length === 0) {
      setErrorMessage("Please enter a password.");
    } else {
      const requestBody = {
        username: emailAddress,
        password: password,
      };

      setLoading(true);
      const [response, responseBody] = (await apiRequest(`${API}/registration/authenticate`, "POST", requestBody)) as [
        Response,
        PostAuthenticateResponseBody
      ];
      setLoading(false);

      if (response.ok && responseBody) {
        setErrorMessage("");
        updateUserInformation(responseBody, true);
        if (responseBody.newPasswordRequired) {
          setCurrentLoginMode("ENTER_PERMANENT_PASSWORD");
        } else if (responseBody.mfaSetupRequired) {
          setCurrentLoginMode("ENTER_MFA_PHONE_NUMBER");
        } else if (responseBody.mfaChallengeRequired) {
          setCurrentLoginMode("ENTER_MFA_CODE");
        } else {
          finalizeUserLogin(responseBody.agreedToEula, props.redirectUrl, responseBody.token);
        }
      } else {
        setErrorMessage(await getApiError(response, "Unable to complete login."));
      }
    }
  }

  // Attempts logging with a refresh token instead of email and password.
  async function handleSubmitRefreshToken(emailAddress: string, refreshToken: string | null): Promise<void> {
    if (refreshToken === null || refreshToken.length === 0) {
      setErrorMessage("Internal server error. Unable to complete token based login.");
    } else if (emailAddress.length === 0) {
      setErrorMessage("Please enter an email address.");
    } else {
      const requestBody = {
        username: emailAddress,
        refreshToken: refreshToken,
      };

      setLoading(true);
      const [response, responseBody] = (await apiRequest(`${API}/registration/authenticate`, "POST", requestBody)) as [
        Response,
        PostAuthenticateResponseBody
      ];
      setLoading(false);

      if (response.ok && responseBody) {
        setErrorMessage("");
        updateUserInformation(responseBody, false);
        if (responseBody.newPasswordRequired) {
          setCurrentLoginMode("ENTER_PERMANENT_PASSWORD");
        } else if (responseBody.mfaSetupRequired) {
          setCurrentLoginMode("ENTER_MFA_PHONE_NUMBER");
        } else if (responseBody.mfaChallengeRequired) {
          setCurrentLoginMode("ENTER_MFA_CODE");
        } else {
          finalizeUserLogin(responseBody.agreedToEula, props.redirectUrl, responseBody.token);
        }
      } else {
        setErrorMessage(await getApiError(response, "Unable to complete login."));
      }
    }
  }

  // Takes a temporary password and sets up a permanent password.
  async function handleUpdatePassword(
    emailAddress: string,
    newPassword: string,
    newConfirmationPassword: string,
    accessToken: string | null,
    sessionToken: string | null
  ): Promise<void> {
    if (passwordIsValid(newPassword, newConfirmationPassword, setErrorMessage)) {
      const requestBody = {
        emailAddress: emailAddress,
        currentPassword: null,
        newPassword: newPassword,
        accessToken: accessToken,
        sessionToken: sessionToken,
      };

      setLoading(true);
      const [response, responseBody] = (await apiRequest(`${API}/registration/password`, "PUT", requestBody)) as [
        Response,
        UpdatePasswordResponseBody
      ];
      setLoading(false);

      if (response.ok && responseBody) {
        setErrorMessage("");
        if (responseBody.mfaChallengeRequired) {
          dispatch(setSessionToken(responseBody.sessionToken));
          setCurrentLoginMode("ENTER_MFA_CODE");
        } else {
          dispatch(setRefreshToken(responseBody.refreshToken));
          void handleSubmitRefreshToken(emailAddress, responseBody.refreshToken);
        }
      } else {
        setErrorMessage(await getApiError(response, "Unable to update password."));
      }
    }
  }

  // Starts the MFA registration process with a user selected phone number.
  async function handleSubmitPhoneNumber(
    emailAddress: string,
    refreshToken: string | null,
    phoneNumber: string,
    callingCode: string
  ): Promise<void> {
    if (phoneNumber.length === 0) {
      setErrorMessage("Please enter a phone number.");
    } else if (callingCode.length === 0) {
      setErrorMessage("Please select a valid calling code.");
    } else {
      const requestBody = {
        emailAddress: emailAddress,
        phoneNumber: `+${callingCode}${phoneNumber}`,
        refreshToken: refreshToken,
      };

      setLoading(true);
      const [response, responseBody] = (await apiRequest(`${API}/mfa/sms-setup`, "POST", requestBody)) as [
        Response,
        MfaSetupResponseBody
      ];
      setLoading(false);

      if (response.ok && responseBody) {
        setErrorMessage("");
        setCurrentLoginMode("ENTER_MFA_VERIFICATION");
      } else {
        setErrorMessage(await getApiError(response, "Unable to register phone number."));
      }
    }
  }

  // Attempts to verify a selected MFA phone number to complete the MFA registration process.
  async function handleVerifyPhoneNumber(
    emailAddress: string,
    refreshToken: string | null,
    verificationCode: string
  ): Promise<void> {
    if (refreshToken === null || refreshToken.length === 0) {
      setErrorMessage("Internal server error. Unable to complete token based login.");
    } else if (verificationCode.length === 0) {
      setErrorMessage("Please enter a verification code.");
    } else {
      const requestBody = {
        emailAddress: emailAddress,
        refreshToken: refreshToken,
        verificationCode: verificationCode,
      };

      setLoading(true);
      const [response, responseBody] = (await apiRequest(`${API}/mfa/verify-phone-number`, "POST", requestBody)) as [
        Response,
        MfaVerifyResponseBody
      ];
      setLoading(false);

      if (response.ok && responseBody) {
        setErrorMessage("");
        // Now that registration is complete, automate trying to login again.
        // This should cause the user to have to enter an authentication code after email and password are accepted.
        void handleSubmitRefreshToken(emailAddress, refreshToken);
      } else {
        setErrorMessage(await getApiError(response, "Unable to verify phone number."));
      }
    }
  }

  // Verify that the MFA code that the user has submitted is correct.
  async function handleSubmitMfaCode(
    emailAddress: string,
    sessionToken: string | null,
    mfaCode: string
  ): Promise<void> {
    if (mfaCode.length === 0) {
      setErrorMessage("Please enter authentication code.");
    } else {
      const requestBody = {
        username: emailAddress,
        sessionToken: sessionToken,
        mfaCode: mfaCode,
      };

      setLoading(true);
      const [response, responseBody] = (await apiRequest(`${API}/registration/authenticate`, "POST", requestBody)) as [
        Response,
        PostAuthenticateResponseBody
      ];
      setLoading(false);

      if (response.ok && responseBody) {
        setErrorMessage("");
        updateUserInformation(responseBody, true);
        finalizeUserLogin(responseBody.agreedToEula, props.redirectUrl, responseBody.token);
      } else {
        setErrorMessage(await getApiError(response, "Unable to verify authentication code."));
      }
    }
  }

  // Store important user information after authenticating.
  function updateUserInformation(
    authenticationResponseBody: PostAuthenticateResponseBody,
    updateRefreshToken: boolean
  ): void {
    setPendingIdToken(authenticationResponseBody.token);
    dispatch(setAccessToken(authenticationResponseBody.accessToken));
    dispatch(setSessionToken(authenticationResponseBody.sessionToken));
    if (updateRefreshToken) {
      dispatch(setRefreshToken(authenticationResponseBody.refreshToken));
    }
    dispatch(
      setCurrentUser({
        userId: authenticationResponseBody.userId,
        userName: authenticationResponseBody.userName,
        emailAddress: authenticationResponseBody.emailAddress,
        originalCompanyId: authenticationResponseBody.companyId,
        originalCompanyName: authenticationResponseBody.companyName,
        originalPermissions: authenticationResponseBody.permissions,
        companyId: authenticationResponseBody.companyId,
        companyName: authenticationResponseBody.companyName,
        permissions: authenticationResponseBody.permissions,
        userCompanyPath: [],
        isCompanyHidden: authenticationResponseBody.isCompanyHidden,
        isPackager: authenticationResponseBody.isPackager,
        mfaEnabled: authenticationResponseBody.mfaEnabled,
      })
    );
  }

  // Complete final login steps after the user has passed all credential checks.
  function finalizeUserLogin(policiesAgreedTo: boolean, redirectUrl: string, pendingIdToken: string | null): void {
    if (!policiesAgreedTo) {
      // Keep this user on the page until they agree to policies.
      setShowEulaAgreementModal(true);
    } else {
      // If user has agreed to all policies, grant the user their token to navigate the application.
      if (pendingIdToken !== null) {
        dispatch(setIdToken(pendingIdToken));
      }
      if (redirectUrl.length > 0) {
        // Bring the user to the page they were attempting to access before being required to login.
        props.onRedirect();
        navigate(redirectUrl);
      } else {
        // Bring the user to the default landing page.
        navigate("/");
      }
    }
  }

  // Cancel all current progress for the user and return to entering an email and password.
  function handleCancel(): void {
    setCurrentLoginMode("ENTER_EMAIL_AND_PASSWORD");
    setEmailAddress("");
    setPassword("");
    setNewPassword("");
    setNewConfirmationPassword("");
    setPhoneNumber("");
    setCallingCode("1");
    setVerificationCode("");
    setMfaCode("");
    setErrorMessage("");
    dispatch(setIdToken(null));
    dispatch(setAccessToken(null));
    dispatch(setRefreshToken(null));
    dispatch(
      setCurrentUser({
        userId: 0,
        userName: "",
        emailAddress: "",
        originalCompanyId: 0,
        originalCompanyName: "",
        originalPermissions: [],
        companyId: 0,
        companyName: "",
        userCompanyPath: [],
        isCompanyHidden: false,
        permissions: [],
        isPackager: false,
        mfaEnabled: false,
      })
    );
  }

  // Go from verifying a phone number for MFA verification to re-selecting the phone number.
  function handleSelectPhoneNumber(): void {
    setCurrentLoginMode("ENTER_MFA_PHONE_NUMBER");
    setVerificationCode("");
    setMfaCode("");
    setErrorMessage("");
  }

  return (
    <div className={styles.body}>
      <Spinner loading={loading} />

      <EulaModal
        showModal={showEulaAgreementModal}
        onClose={() => declineEula()}
        onAccept={() => acceptEula()}
        onDecline={() => declineEula()}
      />

      {currentLoginMode === "ENTER_EMAIL_AND_PASSWORD" && (
        <EmailAndPasswordLoginForm
          errorMessage={errorMessage}
          isForgotPasswordRedirect={props.isForgotPasswordRedirect}
          emailAddress={emailAddress}
          password={password}
          disabled={loading}
          onChangeEmailAddress={(emailAddress) => setEmailAddress(emailAddress)}
          onChangePassword={(password) => setPassword(password)}
          onSubmit={() => handleSubmitEmailAndPassword(emailAddress, password)}
        />
      )}

      {currentLoginMode === "ENTER_PERMANENT_PASSWORD" && (
        <ChangeTemporaryPasswordForm
          errorMessage={errorMessage}
          newPassword={newPassword}
          newConfirmationPassword={newConfirmationPassword}
          disabled={loading}
          onChangeNewPassword={(newPassword) => setNewPassword(newPassword)}
          onChangeNewConfirmationPassword={(newConfirmationPassword) =>
            setNewConfirmationPassword(newConfirmationPassword)
          }
          onCancel={() => handleCancel()}
          onSubmit={() =>
            handleUpdatePassword(emailAddress, newPassword, newConfirmationPassword, accessToken, sessionToken)
          }
        />
      )}

      {currentLoginMode === "ENTER_MFA_PHONE_NUMBER" && (
        <MfaRegistrationPhoneNumberForm
          errorMessage={errorMessage}
          phoneNumber={phoneNumber}
          callingCode={callingCode}
          disabled={loading}
          onChangePhoneNumber={(phoneNumber) => setPhoneNumber(phoneNumber)}
          onChangeCallingCode={(callingCode) => setCallingCode(callingCode)}
          onCancel={() => handleCancel()}
          onSubmit={() => handleSubmitPhoneNumber(emailAddress, refreshToken, phoneNumber, callingCode)}
        />
      )}

      {currentLoginMode === "ENTER_MFA_VERIFICATION" && (
        <MfaRegistrationVerificationForm
          errorMessage={errorMessage}
          verificationCode={verificationCode}
          disabled={loading}
          onChangeVerificationCode={(verificationCode) => setVerificationCode(verificationCode)}
          onCancel={() => handleSelectPhoneNumber()}
          onResend={() => handleSubmitPhoneNumber(emailAddress, refreshToken, phoneNumber, callingCode)}
          onSubmit={() => handleVerifyPhoneNumber(emailAddress, refreshToken, verificationCode)}
        />
      )}

      {currentLoginMode === "ENTER_MFA_CODE" && (
        <MfaLoginForm
          errorMessage={errorMessage}
          mfaCode={mfaCode}
          disabled={loading}
          onChangeMfaCode={(mfaCode) => setMfaCode(mfaCode)}
          onCancel={() => handleCancel()}
          onSubmit={() => handleSubmitMfaCode(emailAddress, sessionToken, mfaCode)}
        />
      )}
    </div>
  );
}

interface Props {
  authorizationError: boolean;
  policyError: boolean;
  isForgotPasswordRedirect: boolean;
  automateAuthentication: boolean;
  redirectUrl: string;
  onRedirect: () => void;
}

interface UpdatePasswordResponseBody {
  message: string;
  refreshToken: string;
  sessionToken: string;
  mfaChallengeRequired: boolean;
}

interface MfaSetupResponseBody {
  message: string;
}

interface MfaVerifyResponseBody {
  message: string;
}

interface EulaResponseBody {
  message: string;
}

type LoginMode =
  | "ENTER_EMAIL_AND_PASSWORD"
  | "ENTER_PERMANENT_PASSWORD"
  | "ENTER_MFA_PHONE_NUMBER"
  | "ENTER_MFA_VERIFICATION"
  | "ENTER_MFA_CODE";
